import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { formatCurrency, formatDate } from '../utils/helpers';
import ClientExposureChart from '../components/ClientExposureChart';
import {
  ArrowLeft, User, Building, Phone, Mail, Calendar as CalIcon, MapPin,
  Users2, FileText, DollarSign, FolderOpen, Clock, Search, Filter, MessageSquare,
  Plus, StickyNote, Trash2, FileWarning, Pencil,
} from 'lucide-react';
import AddClaimDialog from '../components/AddClaimDialog';
import EditClientDialog from '../components/EditClientDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useGlobalDialogs } from '../contexts/GlobalDialogsContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'products', label: 'Products' },
  { key: 'claims', label: 'Claims' },
  { key: 'family', label: 'Family' },
  { key: 'commissions', label: 'Commissions' },
  { key: 'documents', label: 'Documents' },
  { key: 'timeline', label: 'Timeline' },
];

const PRODUCT_TYPE_CHIPS = ['All', 'Health', 'Life', 'Motor', 'Home', 'Travel', 'Mutual Fund', 'Loan', 'Deposit', 'PPF'];

const productTypeToChip = (typeStr, category) => {
  if (category) {
    if (category === 'mutual_fund') return 'Mutual Fund';
    if (category === 'loan') return 'Loan';
    if (['recurring_deposit', 'fixed_deposit'].includes(category)) return 'Deposit';
    if (category === 'ppf') return 'PPF';
  }
  const t = (typeStr || '').toLowerCase();
  if (['term', 'endowment', 'ulip', 'whole life'].some(x => t.includes(x))) return 'Life';
  if (t.includes('health')) return 'Health';
  if (t.includes('motor')) return 'Motor';
  if (t.includes('home')) return 'Home';
  if (t.includes('travel')) return 'Travel';
  return 'Other';
};

const StatCard = ({ label, value, icon: Icon, tint }) => (
  <div className={`rounded-xl border ${tint} p-4`}>
    <div className="flex items-center justify-between">
      <p className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</p>
      <Icon className="h-4 w-4 text-slate-500" />
    </div>
    <p className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope' }}>{value}</p>
  </div>
);

const ClientDetail = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [products, setProducts] = useState([]);
  const [family, setFamily] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [claims, setClaims] = useState([]);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [deletingPolicy, setDeletingPolicy] = useState(null);  // Round 59 · Item 4
  const [docUploadType, setDocUploadType] = useState('pan');
  const [docUploading, setDocUploading] = useState(false);
  const docInputRef = React.useRef(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [productFilter, setProductFilter] = useState('All');
  const [productSearch, setProductSearch] = useState('');
  const [notes, setNotes] = useState([]);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteBody, setNoteBody] = useState('');
  const [noteKind, setNoteKind] = useState('note');
  const [noteSaving, setNoteSaving] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [waBody, setWaBody] = useState('');
  const [waSending, setWaSending] = useState(false);
  const { openAddPolicy } = useGlobalDialogs();

  const load = useCallback(async () => {
    try {
      const [c, pol, fp, fam, com, docs, msgs, ns, cls] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`, { withCredentials: true }),
        axios.get(`${API}/policies?client_id=${clientId}`, { withCredentials: true }),
        axios.get(`${API}/financial-products?client_id=${clientId}`, { withCredentials: true }),
        axios.get(`${API}/clients/${clientId}/family`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/commissions`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/clients/${clientId}/kyc`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/messages?client_id=${clientId}&limit=50`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/clients/${clientId}/notes`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/claims?client_id=${clientId}`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setClient(c.data);
      setPolicies(pol.data || []);
      setProducts(fp.data || []);
      setFamily(fam.data || []);
      const clientPolicyIds = new Set((pol.data || []).map(p => p.policy_id));
      setCommissions((com.data || []).filter(cm => clientPolicyIds.has(cm.policy_id)));
      setDocuments(docs.data || []);
      setMessages(msgs.data || []);
      setNotes(ns.data || []);
      setClaims(cls.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load client');
    } finally { setLoading(false); }
  }, [clientId]);

  const saveNote = async () => {
    if (!noteBody.trim()) { toast.error('Note cannot be empty'); return; }
    setNoteSaving(true);
    try {
      await axios.post(`${API}/clients/${clientId}/notes`, { body: noteBody.trim(), kind: noteKind }, { withCredentials: true });
      toast.success('Note added');
      setNoteBody(''); setNoteKind('note'); setNoteOpen(false);
      const { data } = await axios.get(`${API}/clients/${clientId}/notes`, { withCredentials: true });
      setNotes(data || []);
    } catch (e) { toast.error('Failed to add note'); }
    finally { setNoteSaving(false); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/notes/${noteId}`, { withCredentials: true });
      setNotes(n => n.filter(x => x.note_id !== noteId));
    } catch (e) { toast.error('Delete failed'); }
  };

  const openWaDialog = () => {
    if (!client?.primary_mobile) { toast.error('No mobile number on file for this client'); return; }
    setWaBody(`Hi ${client.name}, this is your agent from InsureGrowth. `);
    setWaOpen(true);
  };

  const sendWa = async () => {
    if (!waBody.trim()) { toast.error('Message body cannot be empty'); return; }
    setWaSending(true);
    try {
      await axios.post(`${API}/messages/send`, {
        client_id: clientId,
        to: client.primary_mobile,
        body: waBody,
        channel: 'whatsapp',
      }, { withCredentials: true });
      toast.success('WhatsApp sent');
      setWaOpen(false);
      // Refresh messages
      const { data } = await axios.get(`${API}/messages?client_id=${clientId}&limit=50`, { withCredentials: true });
      setMessages(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setWaSending(false); }
  };

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;
  if (!client) return <div className="p-6 text-slate-600">Client not found.</div>;

  // Combined product list (insurance + non-insurance)
  const combined = [
    ...policies.map(p => ({
      kind: 'policy',
      chip: productTypeToChip(p.product_type),
      number: p.policy_number,
      name: p.product_name || p.product_type,
      institution: p.insurer,
      // Round 61 · Item 1 — show the premium (total → net → legacy premium_amount)
      // instead of sum_assured, so the client portfolio reflects what the agent
      // actually tracks commercially. sum_assured is still available via raw.
      amount: p.total_premium || p.net_premium || p.premium_amount,
      amount_label: 'Premium',
      premium: p.premium_amount,
      valid_till: p.end_date,
      status: p.status,
      // Round 63 · Fix C — surface loss info to the Products row so the client
      // portfolio can show a red "Lost · <reason>" tag beside the Lapsed badge.
      renewal_status: p.renewal_status || null,
      loss_reason: p.loss_reason || null,
      loss_notes: p.loss_notes || null,
      policy_stage: p.policy_stage || null,
      raw: p,
    })),
    ...products.map(fp => ({
      kind: 'financial',
      chip: productTypeToChip(fp.sub_type, fp.category),
      number: fp.folio_number || fp.account_number || '—',
      name: fp.sub_type || fp.name,
      institution: fp.name,
      amount: fp.amount,
      amount_label: 'Amount',
      premium: fp.emi || null,
      valid_till: fp.maturity_date,
      status: 'Active',
      policy_stage: null,
      renewal_status: null,
      loss_reason: null,
      loss_notes: null,
      raw: fp,
    })),
  ];

  const filteredProducts = combined.filter(p => {
    if (productFilter !== 'All' && p.chip !== productFilter) return false;
    if (productSearch) {
      const q = productSearch.toLowerCase();
      if (!(p.number || '').toLowerCase().includes(q) &&
          !(p.name || '').toLowerCase().includes(q) &&
          !(p.institution || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const totalPremium = policies.reduce((s, p) => s + (p.premium_amount || 0), 0);
  const totalInvestment = products.reduce((s, p) => s + (p.amount || 0), 0);
  const commissionEarned = commissions.reduce((s, c) => s + (c.amount || 0), 0);

  const initials = (client.name || '?').split(' ').map(x => x[0]).slice(0, 2).join('').toUpperCase();
  const dobAge = (() => {
    if (!client.dob) return null;
    const d = new Date(client.dob);
    if (Number.isNaN(d.getTime())) return null;
    const diff = Date.now() - d.getTime();
    return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
  })();

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <button onClick={() => navigate('/clients')} className="flex items-center gap-1 text-sm text-slate-600 hover:text-blue-800" data-testid="client-detail-back">
        <ArrowLeft className="h-4 w-4" /> Back to Clients
      </button>

      {/* Header card */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 rounded-full bg-blue-800 text-white flex items-center justify-center text-xl font-semibold shrink-0" style={{ fontFamily: 'Manrope' }}>
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }} data-testid="client-detail-name">{client.name}</h1>
                {client.client_type === 'Corporate'
                  ? <Badge className="bg-purple-100 text-purple-800"><Building className="h-3 w-3 mr-1" />Corporate</Badge>
                  : <Badge className="bg-blue-100 text-blue-800"><User className="h-3 w-3 mr-1" />Individual</Badge>}
              </div>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-y-1 gap-x-4 text-sm text-slate-600">
                {client.primary_mobile && <div className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{client.primary_mobile}</div>}
                {client.email && <div className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{client.email}</div>}
                {client.dob && <div className="flex items-center gap-1"><CalIcon className="h-3.5 w-3.5" />{formatDate(client.dob)}{dobAge !== null ? ` · ${dobAge}y` : ''}</div>}
                {client.gender && <div>Gender: <span className="font-medium">{client.gender}</span></div>}
                {client.date_of_marriage && <div>Married: <span className="font-medium">{formatDate(client.date_of_marriage)}</span></div>}
                {client.address && <div className="md:col-span-3 flex items-center gap-1 text-slate-700"><MapPin className="h-3.5 w-3.5" />{client.address}</div>}
              </div>
              {client.created_at && (
                <p className="mt-2 text-[11px] text-slate-400">Joined {formatDate(client.created_at)}</p>
              )}
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <Button
                onClick={() => setEditOpen(true)}
                variant="outline"
                size="sm"
                className="border-blue-800 text-blue-800 hover:bg-blue-50"
                data-testid="client-detail-edit-button"
              >
                <Pencil className="h-4 w-4 mr-1.5" /> Edit Client
              </Button>
              <EditClientDialog open={editOpen} onOpenChange={setEditOpen} client={client} onSaved={load} />
              <Button
                onClick={() => openAddPolicy({ clientId })}
                className="bg-indigo-700 hover:bg-indigo-800 text-white"
                size="sm"
                data-testid="client-detail-add-policy-button"
              >
                <Plus className="h-4 w-4 mr-1.5" /> Add Product
              </Button>
              <Button
                onClick={openWaDialog}
                className="bg-green-600 hover:bg-green-700 text-white"
                size="sm"
                data-testid="client-detail-whatsapp-button"
              >
                <MessageSquare className="h-4 w-4 mr-1.5" /> WhatsApp
              </Button>
              <Button
                onClick={() => setNoteOpen(true)}
                variant="outline"
                size="sm"
                data-testid="client-detail-add-note-button"
              >
                <StickyNote className="h-4 w-4 mr-1.5" /> Add Note
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="client-detail-stats">
        <StatCard label="Total Products" value={combined.length} icon={FileText} tint="border-blue-100 bg-blue-50" />
        <StatCard label="Premium + Investment" value={formatCurrency(totalPremium + totalInvestment)} icon={DollarSign} tint="border-emerald-100 bg-emerald-50" />
        <StatCard label="Commission Earned" value={formatCurrency(commissionEarned)} icon={DollarSign} tint="border-amber-100 bg-amber-50" />
        <StatCard label="Family Members" value={family.length} icon={Users2} tint="border-purple-100 bg-purple-50" />
      </div>

      {/* Tabs */}
      <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 flex-wrap" data-testid="client-detail-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded text-xs font-medium ${tab === t.key ? 'bg-white shadow-sm text-blue-800' : 'text-slate-600 hover:text-slate-900'}`}
            data-testid={`client-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="client-tab-overview-panel">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Users2 className="h-4 w-4 text-purple-700" />
                <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Family Group</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {family.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No family members linked yet.</p>
              ) : (
                <ul className="space-y-2">
                  {family.map(m => (
                    <li key={m.family_id} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0">
                      <div>
                        <p className="text-sm font-medium">{m.name}</p>
                        <p className="text-[11px] text-slate-500">{m.relationship}{m.dob ? ` · ${formatDate(m.dob)}` : ''}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-700" />
                <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Products at a Glance</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ClientExposureChart clientId={clientId} compact={false} />
              <p className="mt-3 text-[11px] text-slate-500">Tap "Products" tab for the full list with filters.</p>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === 'products' && (
        <Card className="border-slate-200 shadow-sm" data-testid="client-tab-products-panel">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>All Products ({combined.length})</CardTitle>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
                <Input value={productSearch} onChange={(e) => setProductSearch(e.target.value)} placeholder="Search number, name, insurer…" className="pl-8 w-64" data-testid="products-search" />
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {PRODUCT_TYPE_CHIPS.map(chip => (
                <button
                  key={chip}
                  onClick={() => setProductFilter(chip)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border ${productFilter === chip ? 'bg-blue-800 text-white border-blue-800' : 'bg-white text-slate-700 border-slate-200 hover:border-blue-300'}`}
                  data-testid={`products-chip-${chip.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {chip}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            {filteredProducts.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No products match this filter.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {filteredProducts.map((p, i) => {
                  const isPolicy = p.kind === 'policy';
                  const policyId = isPolicy ? p.raw?.policy_id : null;
                  const raw = p.raw || {};
                  // Round 44 · Item 2: synthesise a tiny timeline from what the
                  // policy already carries — no new backend endpoint needed.
                  const events = [];
                  if (raw.created_at) events.push({ t: raw.created_at, label: 'Added', tone: 'slate' });
                  if (raw.start_date) events.push({ t: raw.start_date, label: 'Started', tone: 'blue' });
                  if (raw.last_renewed_at) events.push({ t: raw.last_renewed_at, label: 'Renewed', tone: 'emerald' });
                  if (raw.lost_at) events.push({ t: raw.lost_at, label: 'Lost', tone: 'red' });
                  if (raw.status === 'Lapsed' && !raw.lost_at) events.push({ t: raw.updated_at, label: 'Lapsed', tone: 'amber' });
                  if (raw.status === 'Matured') events.push({ t: raw.updated_at, label: 'Matured', tone: 'slate' });
                  if (raw.next_due_date) events.push({ t: raw.next_due_date, label: 'Next Due', tone: 'indigo', future: true });
                  events.sort((a, b) => (a.t || '').localeCompare(b.t || ''));
                  const toneClasses = {
                    slate: 'bg-slate-100 text-slate-700 border-slate-200',
                    blue: 'bg-blue-50 text-blue-800 border-blue-200',
                    emerald: 'bg-emerald-50 text-emerald-800 border-emerald-200',
                    red: 'bg-red-50 text-red-800 border-red-200',
                    amber: 'bg-amber-50 text-amber-900 border-amber-200',
                    indigo: 'bg-indigo-50 text-indigo-800 border-indigo-200',
                  };
                  return (
                    <li
                      key={`${p.kind}-${i}`}
                      className={`py-2.5 rounded-md px-2 -mx-2 ${isPolicy ? 'cursor-pointer hover:bg-blue-50/60' : ''}`}
                      onClick={(e) => { if (e.target.closest('[data-nav-skip]')) return; if (isPolicy && policyId) navigate(`/policies/${policyId}`); }}
                      data-testid={isPolicy ? `client-product-open-${policyId}` : `client-product-fin-${i}`}
                      title={isPolicy ? 'Open policy details' : ''}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`text-sm font-medium truncate ${isPolicy ? 'text-blue-800 hover:underline' : 'text-slate-900'}`}>{p.name || 'Untitled'}</p>
                            <Badge variant="outline" className="text-[10px]">{p.chip}</Badge>
                            {/* Round 63 · Fix C — Lapsed + Lost tags on the client product row. */}
                            {isPolicy && p.status === 'Lapsed' && (
                              <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-200" data-testid={`client-product-lapsed-${policyId}`}>
                                Lapsed
                              </span>
                            )}
                            {isPolicy && p.renewal_status === 'lost' && (
                              <span
                                className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-600 text-white"
                                title={p.loss_notes || ''}
                                data-testid={`client-product-lost-${policyId}`}
                              >
                                Lost{p.loss_reason ? ` · ${p.loss_reason}` : ''}
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-500 truncate">{p.number} · {p.institution || '—'}</p>
                        </div>
                        <div className="text-right shrink-0 flex items-center gap-2">
                          <div>
                            <p className="text-[9px] uppercase tracking-wide text-slate-500 leading-none mb-0.5">{p.amount_label || 'Amount'}</p>
                            <p className="text-xs font-semibold text-blue-800">{p.amount ? formatCurrency(p.amount) : '—'}</p>
                            <p className="text-[10px] text-slate-500">{p.valid_till ? `Till ${formatDate(p.valid_till)}` : ''}</p>
                            {isPolicy && p.policy_stage && (
                              <span className="inline-block mt-0.5 text-[9px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-800">
                                {p.policy_stage === 'New Business' ? 'New' : p.policy_stage}
                              </span>
                            )}
                            {isPolicy && !p.policy_stage && (
                              <span className="inline-block mt-0.5 text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">Stage: Not set</span>
                            )}
                          </div>
                          {isPolicy && policyId && (
                            <button
                              type="button"
                              data-nav-skip
                              onClick={(e) => { e.stopPropagation(); setDeletingPolicy({ policy_id: policyId, policy_number: p.number, product_name: p.name, insurer: p.institution }); }}
                              className="p-1 rounded hover:bg-red-50 text-red-600"
                              data-testid={`client-detail-delete-policy-${policyId}`}
                              title="Delete this policy"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      {isPolicy && events.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`policy-timeline-${policyId}`}>
                          {events.map((ev, ei) => (
                            <React.Fragment key={ei}>
                              <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${toneClasses[ev.tone]}`}>
                                <span className="font-semibold">{ev.label}</span>
                                <span className="opacity-70">{ev.t ? formatDate(ev.t) : '—'}</span>
                              </span>
                              {ei < events.length - 1 && <span className="text-slate-300">→</span>}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'claims' && (
        <Card className="border-slate-200 shadow-sm" data-testid="client-tab-claims-panel">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <FileWarning className="h-4 w-4 text-red-600" /> Claims ({claims.length})
              </CardTitle>
              <Button size="sm" onClick={() => setClaimDialogOpen(true)} className="bg-blue-800 hover:bg-blue-900" data-testid="client-claims-new-button">
                <Plus className="h-4 w-4 mr-1.5" /> New Claim
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {claims.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No claims filed for this client yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="client-claims-list">
                {claims.map((c) => (
                  <li
                    key={c.claim_id}
                    className="py-2.5 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 rounded px-2 -mx-2"
                    onClick={() => navigate(`/claims/${c.claim_id}`)}
                    data-testid={`client-claim-row-${c.claim_id}`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-blue-800 hover:underline">{c.claim_number}</p>
                        <Badge variant="outline" className="text-[10px]">{c.status}</Badge>
                      </div>
                      <p className="text-[11px] text-slate-500 truncate">
                        {c.claim_type}{c.policy_number ? ` · ${c.policy_number}` : ''}{c.date_of_loss ? ` · loss ${formatDate(c.date_of_loss)}` : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs font-semibold text-slate-800">{c.claimed_amount ? formatCurrency(c.claimed_amount) : '—'}</p>
                      <p className="text-[10px] text-slate-500">{c.next_followup_date ? `Follow-up ${formatDate(c.next_followup_date)}` : ''}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}


      {tab === 'family' && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-4">
            {family.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No family members yet. Add them from the Manage → Family & KYC screen.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {family.map(m => (
                  <li key={m.family_id} className="py-2 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{m.name}</p>
                      <p className="text-[11px] text-slate-500">{m.relationship}{m.dob ? ` · DOB ${formatDate(m.dob)}` : ''}{m.mobile ? ` · ${m.mobile}` : ''}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'commissions' && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-4">
            {commissions.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6">No commission records for this client yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {commissions.map(c => (
                  <li key={c.commission_id} className="py-2 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">{c.description || 'Commission'}</p>
                      <p className="text-[11px] text-slate-500">{c.date ? formatDate(c.date) : ''} · {c.commission_type || ''}</p>
                    </div>
                    <p className="text-sm font-semibold text-emerald-700">{formatCurrency(c.amount || 0)}</p>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'documents' && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm font-semibold text-slate-800">KYC Documents</p>
                <p className="text-[11px] text-slate-500">Upload PAN, Aadhaar, policy PDFs or other docs. PDF/JPG/PNG/WEBP, max 10MB each.</p>
              </div>
              <div className="flex items-center gap-2">
                <select className="text-xs border border-slate-200 rounded px-2 py-1 bg-white" value={docUploadType} onChange={(e) => setDocUploadType(e.target.value)} data-testid="doc-upload-type">
                  <option value="pan">PAN</option>
                  <option value="aadhaar">Aadhaar</option>
                  <option value="policy">Policy</option>
                  <option value="other">Other</option>
                </select>
                <Button type="button" size="sm" onClick={() => docInputRef.current?.click()} disabled={docUploading} className="bg-blue-800 hover:bg-blue-900" data-testid="doc-upload-button">
                  <FolderOpen className="h-3.5 w-3.5 mr-1" /> {docUploading ? 'Uploading…' : 'Upload'}
                </Button>
                <input ref={docInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    setDocUploading(true);
                    try {
                      const fd = new FormData(); fd.append('file', f);
                      await axios.post(`${API}/clients/${clientId}/kyc?doc_type=${encodeURIComponent(docUploadType)}`, fd, {
                        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
                      });
                      toast.success(`${f.name} uploaded`);
                      const { data: fresh } = await axios.get(`${API}/clients/${clientId}/kyc`, { withCredentials: true });
                      setDocuments(fresh || []);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Upload failed');
                    } finally {
                      setDocUploading(false);
                      if (docInputRef.current) docInputRef.current.value = '';
                    }
                  }}
                  data-testid="doc-upload-file-input" />
              </div>
            </div>
            {documents.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-6" data-testid="documents-empty">No documents uploaded yet.</p>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="documents-list">
                {documents.map(d => (
                  <li key={d.kyc_id || d.file_id} className="py-2 flex items-center justify-between" data-testid={`doc-row-${d.kyc_id || d.file_id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <FolderOpen className="h-4 w-4 text-blue-700" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{(d.doc_type || 'other').toUpperCase()}</p>
                        <p className="text-[11px] text-slate-500 truncate">{d.original_filename || d.filename || d.doc_type}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {d.kyc_id && (
                        <a href={`${API}/kyc/${d.kyc_id}/download`} target="_blank" rel="noreferrer" className="text-xs text-blue-800 hover:underline" data-testid={`doc-open-${d.kyc_id}`}>Open</a>
                      )}
                      {d.kyc_id && (
                        <button type="button" onClick={async () => {
                          if (!window.confirm(`Delete ${d.original_filename || d.doc_type}?`)) return;
                          try {
                            await axios.delete(`${API}/clients/${clientId}/kyc/${d.kyc_id}`, { withCredentials: true });
                            toast.success('Document removed');
                            setDocuments(documents.filter(x => x.kyc_id !== d.kyc_id));
                          } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
                        }} className="text-red-600 hover:text-red-800" data-testid={`doc-delete-${d.kyc_id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'timeline' && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="pt-4 space-y-4">
            {notes.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Notes</p>
                <ul className="divide-y divide-slate-100">
                  {notes.map(n => (
                    <li key={n.note_id} className="py-2 flex items-start justify-between gap-3" data-testid={`note-row-${n.note_id}`}>
                      <div className="flex items-start gap-2 flex-1 min-w-0">
                        <StickyNote className="h-4 w-4 text-amber-600 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <Badge variant="outline" className="text-[9px] mr-1.5">{n.kind}</Badge>
                          <span className="text-sm text-slate-800">{n.body}</span>
                          <p className="text-[10px] text-slate-400 mt-0.5">{formatDate(n.created_at)}</p>
                        </div>
                      </div>
                      <button type="button" onClick={() => deleteNote(n.note_id)} className="text-red-500 hover:text-red-700 shrink-0" data-testid={`note-delete-${n.note_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {messages.length > 0 ? (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Messages</p>
                <ul className="divide-y divide-slate-100">
                  {messages.map(m => (
                    <li key={m.message_id} className="py-2 flex items-start gap-3">
                      <Clock className="h-4 w-4 text-slate-400 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-[11px] uppercase tracking-wider text-slate-500">{m.channel} · {m.direction}</p>
                          <Badge variant="outline" className="text-[9px]">{m.delivery_status || m.status || 'pending'}</Badge>
                        </div>
                        <p className="text-sm text-slate-800 truncate">{m.body}</p>
                        <p className="text-[10px] text-slate-400">{formatDate(m.sent_at)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ) : notes.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-6">No touch-points yet. Send a WhatsApp or add a note to start the timeline.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* WhatsApp dialog */}
      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="max-w-lg" data-testid="client-detail-whatsapp-dialog">
          <DialogHeader>
            <DialogTitle>WhatsApp · {client?.name}</DialogTitle>
            <DialogDescription>To: {client?.primary_mobile}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <textarea
              value={waBody} onChange={(e) => setWaBody(e.target.value)}
              rows={5} maxLength={1500}
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-800 text-sm"
              data-testid="client-detail-wa-body"
            />
            <p className="text-[11px] text-slate-500">{waBody.length}/1500</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setWaOpen(false)}>Cancel</Button>
              <Button onClick={sendWa} disabled={waSending} className="bg-green-600 hover:bg-green-700" data-testid="client-detail-wa-send">
                {waSending ? 'Sending…' : 'Send WhatsApp'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Note dialog */}
      <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
        <DialogContent className="max-w-lg" data-testid="client-detail-add-note-dialog">
          <DialogHeader>
            <DialogTitle>Add Note · {client?.name}</DialogTitle>
            <DialogDescription>Notes appear on the Timeline tab.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2" data-testid="note-kind-toggle">
              {['note', 'call', 'meeting'].map(k => (
                <button key={k} type="button" onClick={() => setNoteKind(k)}
                  className={`px-3 py-1 rounded text-xs font-medium capitalize ${noteKind === k ? 'bg-blue-800 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                  data-testid={`note-kind-${k}`}
                >{k}</button>
              ))}
            </div>
            <textarea
              value={noteBody} onChange={(e) => setNoteBody(e.target.value)}
              rows={5} maxLength={2000} placeholder="Discussed renewal plans, needs Health add-on…"
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-800 text-sm"
              data-testid="client-detail-note-body"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setNoteOpen(false)}>Cancel</Button>
              <Button onClick={saveNote} disabled={noteSaving} className="bg-blue-800 hover:bg-blue-900" data-testid="client-detail-note-save">
                {noteSaving ? 'Saving…' : 'Save Note'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Round 59 · Item 4 — permanent policy delete with cascade confirmation. */}
      <AlertDialog open={!!deletingPolicy} onOpenChange={(o) => { if (!o) setDeletingPolicy(null); }}>
        <AlertDialogContent data-testid="client-detail-delete-alert">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this policy permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong className="text-slate-900">{deletingPolicy?.policy_number}</strong> — {deletingPolicy?.product_name || ''} ({deletingPolicy?.insurer || ''}).<br />
              This will also remove any commission log, renewal reminders, in-app notifications and tasks tied to this policy. The client record stays.
              <br /><br />
              This action can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="client-detail-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const target = deletingPolicy;
                try {
                  const { data } = await axios.delete(`${API}/policies/${target.policy_id}`, { withCredentials: true });
                  const d = data.deleted || {};
                  const extras = [];
                  if (d.commissions)   extras.push(`${d.commissions} commission${d.commissions === 1 ? '' : 's'}`);
                  if (d.reminders)     extras.push(`${d.reminders} reminder log${d.reminders === 1 ? '' : 's'}`);
                  if (d.notifications) extras.push(`${d.notifications} notification${d.notifications === 1 ? '' : 's'}`);
                  if (d.tasks)         extras.push(`${d.tasks} task${d.tasks === 1 ? '' : 's'}`);
                  toast.success(extras.length ? `Policy deleted · cleaned up ${extras.join(', ')}` : 'Policy deleted');
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to delete policy');
                } finally {
                  setDeletingPolicy(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="client-detail-delete-confirm"
            >Delete policy</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New claim from client page — preset client */}
      <AddClaimDialog
        open={claimDialogOpen}
        onClose={() => setClaimDialogOpen(false)}
        onSaved={() => load()}
        presetClientId={clientId}
      />

    </div>
  );
};

export default ClientDetail;
